import { Country, isValidOriginCountry } from './countries-adapter';

import { FormField } from '../models/form';
import {
  MINIMUM_DAYS_FROM_DEPARTURE,
  MAXIMUM_DAYS_FROM_DEPARTURE,
  parseDateSafely,
  getEighteenYearsAgoDate,
  getSixtyNineYears364DaysAgoDate
} from '../components/atoms/date-picker/date-picker-functions';
import { getDaysNumberCommercial } from './prices-calculator';
const MAX_CHARS_DOCUMENTNO = 20;

type ValidateF = (input: string) => boolean;
type ValidateNewCountryF = (input: string, countries: Country[]) => boolean;

export const validateString: ValidateF = (input: string) => {
  const specialCharFormat = /[!@#$%^&*()_+=\[\]{};':"\\|,.<>/?-]+/;
  return !!input && !specialCharFormat.test(input);
};

export const validateName: ValidateF = (input: string) => {
  if (!isValidNameFormat(input)) return false;
  if (!isValidNameLength(input)) return false;
  if (hasProhibitedCharacters(input)) return false;
  if (!hasValidCharacters(input)) return false;
  
  return true;
};

const isValidNameFormat = (input: string): boolean => {
  if (!input || input.trim().length === 0) return false;
  if (input.startsWith(' ') || input.endsWith(' ')) return false;
  if (input.includes('  ')) return false;
  
  return true;
};

const isValidNameLength = (input: string): boolean => {
  return input.length >= 1 && input.length <= 100;
};

const hasProhibitedCharacters = (input: string): boolean => {
  const prohibitedPattern = /[0-9!@#$%^&*()_+=\[\]{};:"\\|,.<>/?]/;
  return prohibitedPattern.test(input);
};

const hasValidCharacters = (input: string): boolean => {
  const validNamePattern = /^[a-zA-ZÀ-ÿĀ-žА-я\u0590-\u05FF\u0600-\u06FF\u0900-\u097F\u0E00-\u0E7F\u0E80-\u0EFF\u0F00-\u0FFF\u1000-\u109F\u1780-\u17FF\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uAC00-\uD7AF\u0300-\u036F\u1AB0-\u1AFF\u1DC0-\u1DFF\u20D0-\u20FF\uFE20-\uFE2F\u16A0-\u16FF\uA720-\uA7FF\uA800-\uA82F\u2070-\u209F\u2C60-\u2C7F\uA640-\uA69F\s'\-]+$/;
  return validNamePattern.test(input);
};

export const validateFutureDate: ValidateF = (input: string) => {
  const inputDate = parseDateSafely(input);
  if (!inputDate) return false;
  
  const now = new Date();
  now.setUTCHours(12, 0, 0, 0);
  
  return inputDate.getTime() > now.getTime();
};

export const validatePhoneNumber: ValidateF = (input: string) => !!input;

export const validatePastDate: ValidateF = (input: string) => {
  const inputDate = parseDateSafely(input);
  if (!inputDate) return false;
  
  const now = new Date();
  now.setUTCHours(12, 0, 0, 0);
  
  return inputDate.getTime() < now.getTime();
};

export const validateEmail: ValidateF = (input: string) => {
  const emailFormat = /^[a-zA-Z0-9._%+-]+(\+[a-zA-Z0-9._%+-]+)?@[a-zA-Z0-9]+([a-zA-Z0-9-]*[a-zA-Z0-9])*(\.[a-zA-Z0-9]+([a-zA-Z0-9-]*[a-zA-Z0-9])*)*\.[a-zA-Z]{2,}$/;
  return emailFormat.test(input);
};

export const validateCountry: ValidateF = (input: string) =>
  isValidOriginCountry(input);

export const validateNewCountry: ValidateNewCountryF = (input: string, countries: Country[]) =>
  countries.some((country) => country.c === input);

export const validateField = (field: FormField, value: string): boolean => {
  switch (field.type) {
    case 'birthday':
      return validatePastDate(value);
    case 'future-date':
      return validateFutureDate(value);
    case 'email':
      return validateEmail(value);
    case 'country':
      return validateCountry(value);
    default:
      return validateString(value);
  }
};

export const limitDocumentNumber = (input: string): string => {
  if (input.length <= MAX_CHARS_DOCUMENTNO) return input;
  return input.slice(0, MAX_CHARS_DOCUMENTNO);
};

export const validateReturnDate = (returnDate: string, departureDate: string): string => {
  let message = '';
  
  const returnDateObj = parseDateSafely(returnDate);
  if (!returnDateObj) {
    return 'format';
  }
  
  const now = new Date();
  now.setUTCHours(12, 0, 0, 0);
  if (returnDateObj.getTime() <= now.getTime()) {
    return 'format';
  }
  
  const departureDateObj = parseDateSafely(departureDate);
  if (!departureDateObj) {
    return 'departure-required';
  }
  
  if (returnDateObj.getTime() <= departureDateObj.getTime()) {
    return 'min-days';
  }
  
  const daysDifference = getDaysNumberCommercial(returnDate, departureDate);
  
  if (daysDifference < MINIMUM_DAYS_FROM_DEPARTURE) {
    message = 'min-days';
  } else if (daysDifference > MAXIMUM_DAYS_FROM_DEPARTURE) {
    message = 'max-days';
  }
  
  return message;
};

export const validateDepartureDate = (returnDate: string, departureDate: string): string => {
  const departureDateObj = parseDateSafely(departureDate);
  if (!departureDateObj) {
    return 'format';
  }
  
  const now = new Date();
  now.setUTCHours(12, 0, 0, 0);
  if (departureDateObj.getTime() <= now.getTime()) {
    return 'format';
  }
  
  return '';
};

export const validateBirthdayAge = (input: string): boolean => {
  const birthDateObj = parseDateSafely(input);
  if (!birthDateObj) return false;
  
  const now = new Date();
  now.setUTCHours(12, 0, 0, 0);
  if (birthDateObj.getTime() >= now.getTime()) return false;
  
  const minDateObj = parseDateSafely(getSixtyNineYears364DaysAgoDate());
  const maxDateObj = parseDateSafely(getEighteenYearsAgoDate());
  
  if (!minDateObj || !maxDateObj) return false;
  
  return birthDateObj.getTime() >= minDateObj.getTime() && 
         birthDateObj.getTime() <= maxDateObj.getTime();
};

export const validateBirthdayWithMessage = (input: string): string => {
  if (!input) return 'format';
  
  const birthDateObj = parseDateSafely(input);
  if (!birthDateObj) return 'format';
  
  const now = new Date();
  now.setUTCHours(12, 0, 0, 0);
  if (birthDateObj.getTime() >= now.getTime()) return 'age';
  
  const minDateObj = parseDateSafely(getSixtyNineYears364DaysAgoDate());
  const maxDateObj = parseDateSafely(getEighteenYearsAgoDate());
  
  if (!minDateObj || !maxDateObj) return 'format';
  
  if (birthDateObj.getTime() < minDateObj.getTime() || 
      birthDateObj.getTime() > maxDateObj.getTime()) {
    return 'age';
  }
  
  return '';
};
